<template>
    <div id="betInAcceptance">

        <!-- ── Fixed bottom acceptance bar ────────────────────────── -->
        <transition name="bar-enter">
            <div v-if="allBets.length > 0" class="acceptance-bar" :class="barStatusClass">

                <!-- Border beam: dot con scia sul bordo (solo pending) -->
                <div v-if="hasPendingBets" class="border-beam" aria-hidden="true"></div>

                <!-- Expanded panel (solo se > 1 bet) -->
                <div class="acceptance-panel" :class="{ 'is-open': expanded && allBets.length > 1 }">
                    <div v-for="item in allBets" :key="item.key"
                         class="panel-row" :class="'panel-row--' + item._type">
                        <div v-if="hasProgress(item.key)"
                             class="auto-close-bar"
                             :style="progressStyle(item)"></div>

                        <div class="panel-row__main">
                            <div class="panel-row__code-area">
                                <span class="panel-row__code">{{ displayCode(item) }}</span>
                                <div v-if="item._type === 'pending' || item._type === 'change'" class="pending-dots">
                                    <div class="circle-pending-revived"></div>
                                    <div class="circle-pending-revived"></div>
                                    <div class="circle-pending-revived"></div>
                                </div>
                            </div>
                            <div class="panel-row__status">
                                <template v-if="item._type === 'pending'">
                                    <span class="status-text status-pending">{{ $t('pending') }}</span>
                                </template>
                                <template v-else-if="item._type === 'accepted'">
                                    <v-icon size="13" color="#22c55e">mdi-checkbox-marked-circle</v-icon>
                                    <span class="status-text status-accepted">{{ $t('betAccepted') }}</span>
                                </template>
                                <template v-else-if="item._type === 'change'">
                                    <span class="status-text status-change">{{ $t(changeLabelKey(item)) }}</span>
                                </template>
                                <template v-else-if="item._type === 'rejected'">
                                    <v-icon size="13" color="#ef4444">mdi-close-circle</v-icon>
                                    <span class="status-text status-rejected">{{ $t('betWasNotAccepted') }}</span>
                                </template>
                            </div>
                        </div>

                        <div v-if="item._type === 'change'" class="panel-row__change-detail">
                            <span>{{ $t('amount') }}: <strong>{{ getSettings.currencySymbol }}{{ item.amount_real | round_digits(2) }}</strong></span>
                            <span>{{ $t('potentialWinnings') }}: <strong>{{ getSettings.currencySymbol }}{{ item.winning_total | round_digits(2) }}</strong></span>
                        </div>

                        <!-- Riproposta admin: elenco compatto delle quote variate (old→new) -->
                        <div v-if="item._type === 'change' && changedOdds(item).length > 0" class="panel-row__odds-changes">
                            <span
                                v-for="odd in changedOdds(item)"
                                :key="'chg-' + (odd.unique_id || odd.outcome_id)"
                                class="odd-change-chip"
                            >
                                <v-icon x-small color="warning">mdi-swap-vertical</v-icon>
                                <span class="odd-change-chip__old">{{ parseFloat(odd.outcome_odd_value_for_order) | odds_type_filter }}</span>
                                <v-icon x-small class="odd-change-chip__arrow">mdi-arrow-right</v-icon>
                                <span class="odd-change-chip__new">{{ parseFloat(odd.outcome_odd_value) | odds_type_filter }}</span>
                            </span>
                        </div>

                        <div class="panel-row__actions">
                            <template v-if="item._type === 'accepted'">
                                <button class="bar-btn bar-btn--ghost" @click="sendPrintMessage(item)">{{ $t('print') }}</button>
                                <button class="bar-btn bar-btn--ghost" @click="removeBetFromAcceptedBets(item.code)">{{ $t('close') }}</button>
                            </template>
                            <template v-else-if="item._type === 'change'">
                                <button class="bar-btn bar-btn--accept" @click="acceptBetReview(item.code)">
                                    <v-icon size="12">mdi-thumb-up</v-icon>
                                    <span>{{ $t('accept') }}</span>
                                </button>
                                <button class="bar-btn bar-btn--reject" @click="rejectBetReview(item.code)">
                                    <v-icon size="12">mdi-thumb-down</v-icon>
                                    <span>{{ $t('reject') }}</span>
                                </button>
                            </template>
                            <template v-else-if="item._type === 'rejected'">
                                <button class="bar-btn bar-btn--ghost" @click="removeBetFromRejectedBets(item.code)">{{ $t('close') }}</button>
                            </template>
                        </div>

                    </div>
                </div>

                <!-- Summary bar (sempre visibile) -->
                <div class="bar-summary"
                     :class="{ 'is-clickable': allBets.length > 1 }"
                     @click="allBets.length > 1 ? toggleExpand() : null">
                    <div v-if="allBets.length === 1 && hasProgress(allBets[0].key)"
                         class="auto-close-bar auto-close-bar--summary"
                         :style="progressStyle(allBets[0])"></div>

                    <!-- Bet singola: mostra tutto inline -->
                    <template v-if="allBets.length === 1">
                        <div class="pending-dots" v-if="allBets[0]._type === 'pending' || allBets[0]._type === 'change'">
                            <div class="circle-pending-revived"></div>
                            <div class="circle-pending-revived"></div>
                            <div class="circle-pending-revived"></div>
                        </div>
                        <v-icon v-else-if="allBets[0]._type === 'accepted'" size="16" color="#22c55e">mdi-checkbox-marked-circle</v-icon>
                        <v-icon v-else-if="allBets[0]._type === 'rejected'" size="16" color="#ef4444">mdi-close-circle</v-icon>

                        <span class="summary-label">{{ $t('acceptanceBet') }}</span>
                        <span class="summary-code">{{ displayCode(allBets[0]) }}</span>
                        <span class="summary-status" :class="'summary-status--' + allBets[0]._type">
                            <template v-if="allBets[0]._type === 'pending'">{{ $t('pending') }}</template>
                            <template v-else-if="allBets[0]._type === 'accepted'">{{ $t('betAccepted') }}</template>
                            <template v-else-if="allBets[0]._type === 'change'">{{ $t(changeLabelKey(allBets[0])) }}</template>
                            <template v-else-if="allBets[0]._type === 'rejected'">{{ $t('betWasNotAccepted') }}</template>
                        </span>

                        <div class="summary-actions">
                            <template v-if="allBets[0]._type === 'accepted'">
                                <button class="bar-btn bar-btn--ghost" @click.stop="sendPrintMessage(allBets[0])">{{ $t('print') }}</button>
                                <button class="bar-btn bar-btn--ghost" @click.stop="removeBetFromAcceptedBets(allBets[0].code)">{{ $t('close') }}</button>
                            </template>
                            <template v-else-if="allBets[0]._type === 'change'">
                                <button class="bar-btn bar-btn--accept" @click.stop="acceptBetReview(allBets[0].code)">
                                    <v-icon size="12">mdi-thumb-up</v-icon>
                                </button>
                                <button class="bar-btn bar-btn--reject" @click.stop="rejectBetReview(allBets[0].code)">
                                    <v-icon size="12">mdi-thumb-down</v-icon>
                                </button>
                            </template>
                            <template v-else-if="allBets[0]._type === 'rejected'">
                                <button class="bar-btn bar-btn--ghost" @click.stop="removeBetFromRejectedBets(allBets[0].code)">{{ $t('close') }}</button>
                            </template>
                        </div>

                        <div v-if="allBets[0]._type === 'change'" class="summary-change-detail">
                            <div class="cart-content">
                                <span class="text-capitalize">{{ $t('amount') }}: </span>
                                <span class="change-value">{{ getSettings.currencySymbol }}{{ allBets[0].amount_real | round_digits(2) }}</span>
                            </div>
                            <div class="cart-content">
                                <span class="text-capitalize">{{ $t('potentialWinnings') }}: </span>
                                <span class="change-value">{{ getSettings.currencySymbol }}{{ allBets[0].winning_total | round_digits(2) }}</span>
                            </div>
                            <div v-if="changedOdds(allBets[0]).length > 0" class="summary-odds-changes">
                                <span
                                    v-for="odd in changedOdds(allBets[0])"
                                    :key="'chg-' + (odd.unique_id || odd.outcome_id)"
                                    class="odd-change-chip"
                                >
                                    <v-icon x-small color="warning">mdi-swap-vertical</v-icon>
                                    <span class="odd-change-chip__old">{{ parseFloat(odd.outcome_odd_value_for_order) | odds_type_filter }}</span>
                                    <v-icon x-small class="odd-change-chip__arrow">mdi-arrow-right</v-icon>
                                    <span class="odd-change-chip__new">{{ parseFloat(odd.outcome_odd_value) | odds_type_filter }}</span>
                                </span>
                            </div>
                        </div>
                    </template>

                    <!-- Più bet: mostra contatore + freccia -->
                    <template v-else>
                        <div class="pending-dots">
                            <div class="circle-pending-revived"></div>
                            <div class="circle-pending-revived"></div>
                            <div class="circle-pending-revived"></div>
                        </div>
                        <span class="summary-label">{{ allBets.length }} {{ $t('acceptanceBet') }}</span>
                        <v-icon class="expand-chevron" :class="{ 'rotated': expanded }" size="20">mdi-chevron-up</v-icon>
                    </template>

                </div>

            </div>
        </transition>

        <!-- ── Review result modal ──────────────────────────────── -->
<!--        <v-dialog transition="dialog-top-transition" max-width="700" v-model="showModalBetReview"-->
<!--                  content-class="modal-on-top">-->
<!--            <v-card class="text-center pt-6 pb-6">-->
<!--                <v-col cols="12">-->
<!--                    <v-icon x-large @click="closeDialogBetReview()">mdi-close-circle</v-icon>-->
<!--                </v-col>-->
<!--                <v-col cols="12" class="message-bet-success">-->
<!--                    <span class="ml-2 text-uppercase">{{ messageModalBetReview }}</span>-->
<!--                </v-col>-->
<!--            </v-card>-->
<!--        </v-dialog>-->

    </div>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';

    export default {
        name: 'BetInAcceptance',
        components: {},
        data: () => ({
            showModalBetReview: false,
            messageModalBetReview: '',
            expanded: false,
            AUTO_CLOSE_SEC: 6,
            autoCloseTimers: {},   // key → timerId
            progressKeys: new Set(), // keys currently showing progress bar
        }),
        computed: {
            ...mapGetters({
                'getAcceptingBets': 'cart/getAcceptingBets',
                'getAcceptedBets': 'cart/getAcceptedBets',
                'getAcceptedBetWithChange': 'cart/getAcceptedBetWithChange',
                'getRejectedBets': 'cart/getRejectedBets',
                'getShortCode': 'settings/getShortCode',
                'getSettings': "cart/getSettings",
            }),
            allBets() {
                const all = [];
                (this.getAcceptingBets || []).forEach(b => all.push({ ...b, _type: 'pending',  key: 'p_' + b.code }));
                (this.getAcceptedBets || []).forEach(b => all.push({ ...b, _type: 'accepted', key: 'a_' + b.code }));
                (this.getAcceptedBetWithChange || []).forEach(b => all.push({ ...b, _type: 'change',   key: 'c_' + b.code }));
                (this.getRejectedBets || []).forEach(b => all.push({ ...b, _type: 'rejected', key: 'r_' + b.code }));
                return all;
            },
            hasPendingBets() {
                return this.allBets.some(b => b._type === 'pending');
            },
            barStatusClass() {
                if (!this.allBets.length) return '';
                const types = this.allBets.map(b => b._type);
                if (types.includes('rejected')) return 'bar--rejected';
                if (types.includes('accepted')) return 'bar--accepted';
                if (types.includes('change'))   return 'bar--change';
                return '';
            },
        },
        watch: {
            allBets(val, old) {
                if (val.length <= 1) this.expanded = false;

                const autoTypes = ['accepted', 'rejected'];
                const oldKeys = new Set(old.map(b => b.key));

                val.forEach(item => {
                    if (!autoTypes.includes(item._type)) return;
                    if (oldKeys.has(item.key)) return;           // già presente
                    if (this.autoCloseTimers[item.key]) return;  // timer già attivo

                    this.progressKeys = new Set([...this.progressKeys, item.key]);

                    this.autoCloseTimers[item.key] = setTimeout(() => {
                        this._autoRemove(item);
                        delete this.autoCloseTimers[item.key];
                        const next = new Set(this.progressKeys);
                        next.delete(item.key);
                        this.progressKeys = next;
                    }, this.AUTO_CLOSE_SEC * 1000);
                });

                // Cancella timer per item rimossi manualmente
                const currentKeys = new Set(val.map(b => b.key));
                Object.keys(this.autoCloseTimers).forEach(k => {
                    if (!currentKeys.has(k)) {
                        clearTimeout(this.autoCloseTimers[k]);
                        delete this.autoCloseTimers[k];
                    }
                });
            }
        },
        beforeDestroy() {
            Object.values(this.autoCloseTimers).forEach(id => clearTimeout(id));
        },
        methods: {
            ...mapActions({
                'removeBetFromAcceptedBets': 'cart/removeBetFromAcceptedBets',
                'removeBetFromRejectedBets': 'cart/removeBetFromRejectedBets',
                'sendReviewBet': 'cart/sendReviewBet',
                'removeBetFromAcceptedWithChange': 'cart/removeBetFromAcceptedWithChange',
                'acceptsRevisedBet': 'cart/acceptsRevisedBet',
                'rejectRevisedBet': 'cart/rejectRevisedBet'
            }),
            toggleExpand() {
                this.expanded = !this.expanded;
            },
            hasProgress(key) {
                return this.progressKeys.has(key);
            },
            progressStyle(item) {
                const color = item._type === 'accepted' ? '#22c55e' : '#ef4444';
                return {
                    '--progress-color': color,
                    '--progress-dur': this.AUTO_CLOSE_SEC + 's',
                };
            },
            _autoRemove(item) {
                if (item._type === 'accepted') this.removeBetFromAcceptedBets(item.code);
                else if (item._type === 'rejected') this.removeBetFromRejectedBets(item.code);
            },
            displayCode(bet) {
                return this.getShortCode ? (bet.betCode || bet.code) : bet.code;
            },
            // True quando l'admin ha proposto una quota diversa da quella richiesta
            // dal player (outcome_odd_value_for_order = originale, outcome_odd_value = nuova).
            hasOddChanged(odd) {
                if (!odd || odd.outcome_odd_value_for_order == null) return false;
                const requested = parseFloat(odd.outcome_odd_value_for_order);
                const proposed = parseFloat(odd.outcome_odd_value);
                if (isNaN(requested) || isNaN(proposed)) return false;
                return Math.abs(requested - proposed) > 0.001;
            },
            // Ritorna solo le odds che hanno subito variazione (per la riproposta).
            changedOdds(item) {
                if (!item || !Array.isArray(item.odds)) return [];
                return item.odds.filter(this.hasOddChanged);
            },
            // True quando l'importo proposto dall'admin (amount_real) differisce
            // dall'importo originale del player (amount dallo snapshot).
            hasAmountChanged(item) {
                if (!item || item.amount == null || item.amount_real == null) return false;
                const original = parseFloat(item.amount);
                const proposed = parseFloat(item.amount_real);
                if (isNaN(original) || isNaN(proposed)) return false;
                return Math.abs(original - proposed) > 0.001;
            },
            // Ritorna la chiave i18n corretta in base a cosa è cambiato nella riproposta.
            changeLabelKey(item) {
                const amt = this.hasAmountChanged(item);
                const odd = this.changedOdds(item).length > 0;
                if (amt && odd) return 'proposalChangeAmountAndOdd';
                if (odd)       return 'proposalChangeOdd';
                return 'proposalChangeAmount';
            },
            async acceptBetReview(betCode) {
                const result = await this.acceptsRevisedBet({code: betCode});
                this.messageModalBetReview = (result?.data?.result)
                    ? this.$t('betRevivedAccepted')
                    : this.$t('Something went wrong!');
                this.showModalBetReview = true;
                this.removeBetFromAcceptedWithChange(betCode);
            },
            async rejectBetReview(betCode) {
                const result = await this.rejectRevisedBet({code: betCode});
                this.messageModalBetReview = (result?.data?.result)
                    ? this.$t('betRevivedNotAccepted')
                    : this.$t('Something went wrong!');
                this.showModalBetReview = true;
                this.removeBetFromAcceptedWithChange(betCode);
            },
            closeDialogBetReview() {
                this.showModalBetReview = false;
                this.messageModalBetReview = '';
            },
            sendPrintMessage(bet) {
                this.$root.$emit('print_this_bet', bet);
            }
        },
    };
</script>

<style scoped>

/* ── Fixed bottom bar ────────────────────────────────────── */
.acceptance-bar {
    position: fixed;
    bottom: 20px;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
    min-width: 340px;
    max-width: min(680px, calc(100vw - 32px));
    z-index: 9998;
    display: flex;
    flex-direction: column;
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.bar--accepted {
    border-color: rgba(34, 197, 94, 0.6);
    box-shadow:
        0 0 0 1px rgba(34, 197, 94, 0.25),
        0 0 24px rgba(34, 197, 94, 0.25),
        0 8px 32px rgba(0, 0, 0, 0.5);
    animation: pulse-accepted 1.8s ease-in-out 2;
}

.bar--rejected {
    border-color: rgba(239, 68, 68, 0.6);
    box-shadow:
        0 0 0 1px rgba(239, 68, 68, 0.25),
        0 0 24px rgba(239, 68, 68, 0.25),
        0 8px 32px rgba(0, 0, 0, 0.5);
    animation: pulse-rejected 1.8s ease-in-out 2;
}

.bar--change {
    border-color: rgba(245, 158, 11, 0.5);
    box-shadow:
        0 0 0 1px rgba(245, 158, 11, 0.2),
        0 0 18px rgba(245, 158, 11, 0.2),
        0 8px 32px rgba(0, 0, 0, 0.5);
}

@keyframes pulse-accepted {
    0%, 100% { box-shadow: 0 0 0 1px rgba(34,197,94,0.25), 0 0 24px rgba(34,197,94,0.25), 0 8px 32px rgba(0,0,0,0.5); }
    50%       { box-shadow: 0 0 0 3px rgba(34,197,94,0.4),  0 0 40px rgba(34,197,94,0.45), 0 8px 32px rgba(0,0,0,0.5); }
}

@keyframes pulse-rejected {
    0%, 100% { box-shadow: 0 0 0 1px rgba(239,68,68,0.25), 0 0 24px rgba(239,68,68,0.25), 0 8px 32px rgba(0,0,0,0.5); }
    50%       { box-shadow: 0 0 0 3px rgba(239,68,68,0.4),  0 0 40px rgba(239,68,68,0.45), 0 8px 32px rgba(0,0,0,0.5); }
}

/* ── Auto-close countdown bar ────────────────────────────── */
.auto-close-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: 100%;
    background: var(--progress-color, #22c55e);
    transform-origin: left center;
    animation: countdown-shrink var(--progress-dur, 6s) linear forwards;
    border-radius: 0 0 10px 10px;
}

.auto-close-bar--summary {
    bottom: 0;
    border-radius: 0 0 10px 10px;
}

@keyframes countdown-shrink {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
}

/* ── Border beam (pending) ───────────────────────────────── */
.border-beam {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    overflow: hidden;
    pointer-events: none;
    z-index: 0;
}

.border-beam::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    left: -50%;
    top: -50%;
    background: conic-gradient(
        from 0deg,
        transparent 0deg,
        transparent 250deg,
        rgba(245, 158, 11, 0.15) 290deg,
        rgba(245, 158, 11, 0.55) 330deg,
        rgba(255, 235, 120, 0.95) 348deg,
        rgba(255, 255, 255, 1)   353deg,
        rgba(255, 235, 120, 0.95) 356deg,
        transparent 360deg
    );
    animation: beam-spin 2.2s linear infinite;
}

.border-beam::after {
    content: '';
    position: absolute;
    inset: 1.5px;
    border-radius: 8.5px;
    background: var(--bg-card);
}

@keyframes beam-spin {
    to { transform: rotate(360deg); }
}

/* Il contenuto deve stare sopra al beam */
.acceptance-panel,
.bar-summary {
    position: relative;
    z-index: 1;
}

/* ── Expanded panel ──────────────────────────────────────── */
.acceptance-panel {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.35s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.acceptance-panel.is-open {
    max-height: 480px;
    overflow-y: auto;
}

/* ── Panel row ───────────────────────────────────────────── */
.panel-row {
    padding: 10px 16px;
    border-bottom: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.panel-row:last-child { border-bottom: none; }

.panel-row__main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.panel-row__code-area {
    display: flex;
    align-items: center;
    gap: 8px;
}

.panel-row__code {
    font-size: var(--font-size-3xl);
    font-weight: 700;
    color: var(--text-platform);
}

.panel-row__status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--font-size-xl);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex-shrink: 0;
}

.panel-row__change-detail {
    display: flex;
    gap: 12px;
    font-size: var(--font-size-2xl);
    color: var(--text-op-50-platform);
}

.panel-row__actions {
    display: flex;
    gap: 6px;
}

/* ── Summary bar ─────────────────────────────────────────── */
.bar-summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 16px;
    min-height: 48px;
}
.bar-summary.is-clickable {
    cursor: pointer;
    user-select: none;
}
.bar-summary.is-clickable:hover { background: var(--bg-cell); }

.summary-label {
    font-size: var(--font-size-2xl);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-op-50-platform);
    flex-shrink: 0;
}

.summary-code {
    font-size: var(--font-size-3xl);
    font-weight: 700;
    color: var(--text-platform);
}

.summary-status {
    font-size: var(--font-size-xl);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex: 1;
}

.summary-actions {
    display: flex;
    gap: 6px;
    margin-left: auto;
}

/* ── Single-bet change detail row ────────────────────────── */
.summary-change-detail {
    flex-basis: 100%;
    display: flex;
    gap: 16px;
    padding-top: 6px;
    border-top: 1px solid var(--border-color);
    margin-top: 2px;
}

.cart-content {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--font-size-2xl);
    color: var(--text-op-50-platform);
}

.change-value {
    font-weight: 700;
    color: #f59e0b;
}

/* ── Riproposta: chip old→new per quota variata ────────── */
.panel-row__odds-changes,
.summary-odds-changes {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}
.summary-odds-changes {
    flex-basis: 100%;
    padding-top: 6px;
    border-top: 1px dashed rgba(245, 158, 11, 0.25);
}
.odd-change-chip {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: var(--font-size-xl);
    font-weight: 700;
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 4px;
    padding: 2px 6px;
    line-height: 1;
}
.odd-change-chip__old {
    color: var(--text-op-50-platform);
    text-decoration: line-through;
}
.odd-change-chip__arrow {
    color: var(--text-op-50-platform) !important;
    margin: 0 1px;
}
.odd-change-chip__new {
    color: #f59e0b;
    font-weight: 800;
}

.expand-chevron {
    margin-left: auto;
    color: var(--text-op-50-platform) !important;
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.expand-chevron.rotated { transform: rotate(180deg); }

/* ── Status colors ───────────────────────────────────────── */
.status-pending, .summary-status--pending   { color: #f59e0b; }
.status-accepted, .summary-status--accepted { color: #22c55e; }
.status-change, .summary-status--change     { color: #f59e0b; }
.status-rejected, .summary-status--rejected { color: #ef4444; }

/* ── Pending animated dots ───────────────────────────────── */
.pending-dots {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}
.circle-pending-revived {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f59e0b;
    animation: dot-pulse 1.2s ease-in-out infinite;
}
.circle-pending-revived:nth-child(2) { animation-delay: 0.2s; }
.circle-pending-revived:nth-child(3) { animation-delay: 0.4s; }

@keyframes dot-pulse {
    0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
    40%           { opacity: 1;    transform: scale(1.15); }
}

/* ── Buttons ─────────────────────────────────────────────── */
.bar-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 5px;
    border: none;
    cursor: pointer;
    font-size: var(--font-size-md);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    transition: background 0.15s, border-color 0.15s;
}
.bar-btn--ghost {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.2);
    color: var(--text-platform);
}
.bar-btn--ghost:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.4); }

.bar-btn--accept {
    background: rgba(34, 197, 94, 0.15);
    border: 1px solid rgba(34, 197, 94, 0.35);
    color: #22c55e;
}
.bar-btn--accept:hover { background: rgba(34, 197, 94, 0.28); }

.bar-btn--reject {
    background: rgba(239, 68, 68, 0.12);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
}
.bar-btn--reject:hover { background: rgba(239, 68, 68, 0.25); }

/* ── Bar entry animation ─────────────────────────────────── */
.bar-enter-enter-active, .bar-enter-leave-active {
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), opacity 0.25s ease;
}
.bar-enter-enter, .bar-enter-leave-to {
    transform: translateY(120%);
    opacity: 0;
}

/* ── Mobile: full width, 88px dal basso ──────────────────── */
@media (max-width: 600px) {
    .acceptance-bar {
        left: 0;
        right: 0;
        bottom: 88px;
        transform: none;
        width: auto;
        min-width: unset;
        max-width: unset;
        border-radius: 12px 12px 0 0;
    }
    .bar-enter-enter, .bar-enter-leave-to {
        transform: translateY(120%);
    }
}

/* ── Modal ───────────────────────────────────────────────── */
.message-bet-success {
    font-size: var(--font-size-4xl);
    font-weight: 600;
    color: #e2e8f0;
    padding: 12px 16px;
}
</style>
